import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import type { DealValidationState } from '../types/dealValidationTypes';
import type { Dealership } from '@makemydeal/dr-activities-dealership-picker';

/**
 * Determines if role assumption should be initiated based on Manager View conditions
 *
 * @param isEmployee - Whether the user is an employee
 * @param enableRoleAssumptionInMV - Feature toggle for role assumption in Manager View
 * @param mvDealValidation - Feature toggle for deal validation in Manager View
 * @param dealValidation - Current deal validation state
 * @param requestStatus - Current API request status from dealership picker
 * @param dealerships - Array of available dealerships
 * @returns True if role assumption should be initiated
 */
export const shouldInitiateRoleAssumption = (
    isEmployee: boolean,
    enableRoleAssumptionInMV: boolean,
    mvDealValidation: boolean,
    dealValidation: DealValidationState | null | undefined,
    requestStatus: ApiRequestStatus,
    dealerships: Dealership[],
    assumedUserName: string
): boolean => {
    return (
        isEmployee &&
        enableRoleAssumptionInMV &&
        mvDealValidation &&
        !dealValidation?.valid &&
        requestStatus === ApiRequestStatus.Success &&
        dealerships.length > 0 &&
        assumedUserName.length > 0
    );
};

/**
 * Determines if role assumption should be cleared based on Manager View conditions
 *
 * @param isEmployee - Whether the user is an employee
 * @param enableRoleAssumptionInMV - Feature toggle for role assumption in Manager View
 * @param mvDealValidation - Feature toggle for deal validation in Manager View
 * @param dealValidation - Current deal validation state
 * @param hasDealershipMissing - Whether dealership is missing from validation
 * @param requestStatus - Current API request status from dealership picker
 * @param dealerships - Array of available dealerships
 * @returns True if role assumption should be initiated
 */
export const shouldClearRoleAssumption = (
    isEmployee: boolean,
    enableRoleAssumptionInMV: boolean,
    mvDealValidation: boolean,
    dealValidation: DealValidationState | null | undefined,
    requestStatus: ApiRequestStatus,
    dealerships: Dealership[]
): boolean => {
    return (
        isEmployee &&
        enableRoleAssumptionInMV &&
        mvDealValidation &&
        !dealValidation?.valid &&
        requestStatus === ApiRequestStatus.Success &&
        dealerships.length === 0
    );
};

/**
 * Determines if the user is invalid for role assumption
 * @param isEmployee - Whether the user is an employee
 * @param enableRoleAssumptionInMV - Whether role assumption is enabled in Manager View
 * @param requestStatus - Current API request status
 * @param dealerships - Array of available dealerships
 * @param isDealValid - Whether the current deal is valid
 * @returns True if the user is invalid (employee with no dealerships)
 */
export const isUserInvalidForRoleAssumption = (
    isEmployee: boolean,
    enableRoleAssumptionInMV: boolean,
    requestStatus: ApiRequestStatus,
    dealerships: Dealership[],
    isDealValid: boolean
): boolean => {
    return (
        isEmployee &&
        enableRoleAssumptionInMV &&
        requestStatus === ApiRequestStatus.Success &&
        dealerships.length === 0 &&
        !isDealValid
    );
};

/**
 * Determines if the role assumption is in a loading state
 * @param isEmployee - Whether the user is an employee
 * @param enableRoleAssumptionInMV - Whether role assumption is enabled in Manager View
 * @param requestStatus - Current API request status
 * @returns True if role assumption is loading
 */
export const isRoleAssumptionLoading = (
    isEmployee: boolean,
    enableRoleAssumptionInMV: boolean,
    requestStatus: ApiRequestStatus,
    assumedUserName: string
): boolean => {
    return isEmployee && enableRoleAssumptionInMV && requestStatus === ApiRequestStatus.Loading && assumedUserName.length > 0;
};
